import React, { useState, useEffect, useCallback, useRef } from 'react';
import gateApi from '../api';
import { STATUS_CONFIG } from '../constants';
import {
  C, FormShell, Field, Select, TextArea, SubmitButton, Banner,
} from './FormKit';

const LOAD_TYPES = ['10mm', '20mm', '30mm','40mm','60mm', 'DUST', 'BOULDER', 'WMM', 'GSB','BC-1-Dust', 'OTHER'];

function fmtDuration(mins) {
  if (mins == null) return '—';
  const h = Math.floor(mins / 60), m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export default function ExitForm() {
  const [vehicles, setVehicles]   = useState([]);
  const [selTx, setSelTx]         = useState('');
  
  // Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Operator-triggered capture: the photo lives on the Pi until this form is
  // submitted, and only then is it uploaded and tagged with the vehicle. Nothing
  // has to be matched to a truck afterwards, so a photo can't land on the wrong trip.
  const [capture, setCapture]     = useState(null);   // { captureId, s3Key, sizeKb, thumbnail }
  const [capturing, setCapturing] = useState(false);
  const [camErr, setCamErr]       = useState(null);
  const [load, setLoad]           = useState('10mm');
  const [notes, setNotes]         = useState('');
  const [busy, setBusy]           = useState(false);
  const [result, setResult]       = useState(null);
  const [tick, setTick]           = useState(0);      // forces "time inside" recompute

  const selected = vehicles.find(v => v.transactionId === selTx);
  // PENDING_EXIT is the normal ready state; ACTIVE is the legacy pre-deploy
  // cutover status (exit.allow-legacy-active) — both are actually exitable.
  // Everything else (still at tare/loading/challan) is visible but not ready.
  const readyForExit = selected && (selected.lifecycleStatus === 'PENDING_EXIT' || selected.lifecycleStatus === 'ACTIVE');

  const loadVehicles = useCallback(async () => {
    // Every in-progress vehicle, not just PENDING_EXIT ones — operators were
    // searching for a plate at the exit gate and finding nothing, with no way
    // to tell whether it just hadn't been entered or was stuck at an earlier
    // stage (tare/loading/challan). Now they see it either way, with its
    // actual stage, and can only complete the exit once it's really ready.
    try { setVehicles(await gateApi.getInProgressVehicles()); }
    catch { /* keep last list */ }
  }, []);

  // Take the photo now. Replacing an earlier capture tells the Pi to delete it,
  // so only the photo the operator actually approves is ever uploaded.
  const takePhoto = useCallback(async () => {
    setCapturing(true); setCamErr(null);
    const previous = capture?.captureId;
    try {
      const c = await gateApi.captureExitPhoto();
      setCapture(c);
      if (previous) gateApi.discardExitPhoto(previous).catch(() => {});
    } catch (e) {
      const data = e.response?.data;
      setCamErr(data?.message || data?.error || 'Could not reach the gate camera.');
    } finally {
      setCapturing(false);
    }
  }, [capture]);

  // Handle clicking outside the custom dropdown to close it
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    loadVehicles();
    const v = setInterval(loadVehicles, 15000);
    const t = setInterval(() => setTick(x => x + 1), 30000);
    return () => { clearInterval(v); clearInterval(t); };
  }, [loadVehicles]);

  // Filter vehicles dynamically based on what user types
  const filteredVehicles = vehicles.filter(v =>
    v.licensePlate?.toUpperCase().includes(searchQuery.toUpperCase().trim())
  );

  function selectVehicle(v) {
    setSelTx(v.transactionId);
    setSearchQuery(v.licensePlate); // Fill input field with exact selected item
    setShowDropdown(false);
  }

  async function submit() {
    if (!selTx) { setResult({ ok: false, msg: 'Select the vehicle first.' }); return; }
    if (!readyForExit) { setResult({ ok: false, msg: 'This vehicle has not cleared the weighbridge/challan yet.' }); return; }
    setBusy(true); setResult(null);
    try {
      const r = await gateApi.completeExit(selTx, {
        captureId: capture?.captureId || null,
        s3Key: capture?.s3Key || null,
        freightClassification: load,
        operatorNotes: notes,
      });
      setResult({
        ok: true,
        msg: `${r.plate} logged OUT · load ${r.freightClassification || '—'} · inside ${fmtDuration(r.durationMinutes)}` +
             (r.photoAttached ? (r.uploadQueued ? ' · photo uploading' : ' · photo NOT uploading — check the camera')
                              : ' · no photo'),
      });
      setSelTx(''); setNotes(''); setCapture(null); setCamErr(null); setSearchQuery('');
      loadVehicles();
    } catch (e) {
      const data = e.response?.data;
      setResult({ ok: false, msg: data?.message || data?.error || 'Could not complete exit.' });
    } finally {
      setBusy(false);
    }
  }

  let minsInside = selected?.minutesInside;
  if (selected?.entryTime) {
    minsInside = Math.max(0, Math.round((Date.now() - new Date(selected.entryTime)) / 60000));
  }
  // eslint-disable-next-line no-unused-expressions
  tick;

  return (
    <FormShell title="Exit" accent={C.teal}>
      <h1 style={{ fontSize: 26, fontWeight: 700, margin: '0 0 4px' }}>Vehicle exit</h1>
      <p style={{ color: C.muted, fontSize: 14, margin: '0 0 26px' }}>
        Photograph the load, pick the vehicle, confirm the load type, and log it out.
      </p>

      {result && <Banner type={result.ok ? 'success' : 'error'}>{result.msg}</Banner>}

      {/* Operator-triggered load photo */}
      <Field label="Load photo"
             hint="Press when the truck is under the exit camera. Retake as often as you like — only the photo shown here is kept.">
        <div style={{
          background: C.panel2, border: `1px solid ${C.border}`, borderRadius: 12,
          overflow: 'hidden', minHeight: 180, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}>
          {capture?.thumbnail ? (
            <img src={`data:image/jpeg;base64,${capture.thumbnail}`} alt="captured load"
              style={{ width: '100%', maxHeight: 320, objectFit: 'contain', display: 'block' }} />
          ) : (
            <div style={{ color: C.faint, fontFamily: C.mono, fontSize: 13, padding: 40, textAlign: 'center' }}>
              {capturing ? 'Capturing…' : camErr ? camErr : 'No photo yet — press “Take photo”.'}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" onClick={takePhoto} disabled={capturing}
            style={{
              background: capture ? C.panel2 : C.teal, color: capture ? C.text : '#04201c',
              border: `1px solid ${capture ? C.border : C.teal}`, borderRadius: 10,
              padding: '10px 18px', fontSize: 14, fontWeight: 700,
              cursor: capturing ? 'wait' : 'pointer', opacity: capturing ? 0.6 : 1,
            }}>
            {capturing ? 'Capturing…' : capture ? 'Retake photo' : 'Take photo'}
          </button>
          {capture && (
            <span style={{ fontFamily: C.mono, fontSize: 12, color: C.muted }}>
              {capture.sizeKb} KB · stays on the camera until you submit
            </span>
          )}
        </div>

        {camErr && capture && (
          <div style={{ marginTop: 8, fontSize: 12, color: C.amber }}>{camErr}</div>
        )}
      </Field>

      {/* Searchable Vehicle Input Component */}
      <Field label="Vehicle leaving"
             hint={vehicles.length ? `${vehicles.length} vehicle(s) inside, any stage` : 'No vehicles currently inside'}>
        <div ref={dropdownRef} style={{ position: 'relative' }}>
          <input
            type="text"
            placeholder={vehicles.length ? "Type to search number plate... (e.g. BR01)" : "none inside"}
            disabled={!vehicles.length}
            value={searchQuery}
            onFocus={() => setShowDropdown(true)}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setSelTx(''); // Reset selected transaction ID if operator modifies search manually
            }}
            style={{
              width: '100%', boxSizing: 'border-box', background: C.panel2,
              border: `1px solid ${C.border}`, borderRadius: 10, color: C.text,
              padding: '14px 16px', fontSize: 16, fontFamily: C.mono, outline: 'none',
              textTransform: 'uppercase', letterSpacing: '0.04em'
            }}
          />
          
          {showDropdown && vehicles.length > 0 && (
            <div style={{
              position: 'absolute', top: '102%', left: 0, right: 0,
              background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10,
              maxHeight: 220, overflowY: 'auto', zIndex: 10,
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)', padding: '6px 0'
            }}>
              {filteredVehicles.length > 0 ? (
                filteredVehicles.map(v => {
                  // displayStatus splits "billed, unpaid" out of PENDING_CHALLAN —
                  // both share that lifecycleStatus, so the raw value alone always
                  // reads "Awaiting Challan" (see /transactions/in-progress).
                  const stage = STATUS_CONFIG[v.displayStatus || v.lifecycleStatus];
                  return (
                    <div
                      key={v.transactionId}
                      onClick={() => selectVehicle(v)}
                      style={{
                        padding: '12px 16px', cursor: 'pointer', fontSize: 14,
                        fontFamily: C.sans, color: C.text, display: 'flex',
                        justifyContent: 'space-between', alignItems: 'center', gap: 10,
                        transition: 'background 0.1s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = C.panel2}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                    >
                      <span style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
                        <span style={{ fontFamily: C.mono, fontWeight: 700 }}>{v.licensePlate}</span>
                        <span style={{ color: C.muted, fontSize: 12, whiteSpace: 'nowrap' }}>
                          {v.vehicleType || 'VEHICLE'} · {fmtDuration(v.minutesInside)} inside
                        </span>
                      </span>
                      {stage && (
                        <span style={{
                          flexShrink: 0, fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap',
                          padding: '3px 9px', borderRadius: 20, letterSpacing: '0.03em',
                          background: stage.bg, color: stage.color,
                        }}>{stage.label}</span>
                      )}
                    </div>
                  );
                })
              ) : (
                <div style={{ padding: '14px 16px', color: C.faint, fontSize: 13, fontFamily: C.sans }}>
                  No matching number plates found
                </div>
              )}
            </div>
          )}
        </div>
      </Field>

      {selected && (
        <div style={{
          background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10,
          padding: '12px 16px', marginBottom: 20, display: 'flex',
          gap: 24, flexWrap: 'wrap', alignItems: 'center', fontSize: 13,
        }}>
          <span style={{ color: C.muted }}>Entered:&nbsp;
            <span style={{ color: C.text, fontFamily: C.mono }}>
              {selected.entryTime ? new Date(selected.entryTime).toLocaleTimeString() : '—'}</span></span>
          <span style={{ color: C.muted }}>Time inside:&nbsp;
            <span style={{ color: C.teal, fontFamily: C.mono }}>{fmtDuration(minsInside)}</span></span>
          {selected.expectedLoad && <span style={{ color: C.muted }}>Expected:&nbsp;
            <span style={{ color: C.text }}>{selected.expectedLoad}</span></span>}
          {STATUS_CONFIG[selected.displayStatus || selected.lifecycleStatus] && (
            <span style={{
              marginLeft: 'auto', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap',
              padding: '3px 9px', borderRadius: 20, letterSpacing: '0.03em',
              background: STATUS_CONFIG[selected.displayStatus || selected.lifecycleStatus].bg,
              color: STATUS_CONFIG[selected.displayStatus || selected.lifecycleStatus].color,
            }}>{STATUS_CONFIG[selected.displayStatus || selected.lifecycleStatus].label}</span>
          )}
        </div>
      )}

      {selected && !readyForExit && (
        <Banner type="error">
          {selected.licensePlate} hasn't cleared the weighbridge/challan yet — it can't be logged out until its
          status reaches "Awaiting Exit".
        </Banner>
      )}

      {/* Confirmed load */}
      <Field label="Actual load (confirm or correct)">
        <Select value={load} onChange={setLoad} options={LOAD_TYPES} />
      </Field>

      {/* The "manual override" checkbox lived here. It asked whether the operator
          disagreed with the AI — but the AI guess now arrives after the photo is
          uploaded, which is after this form is submitted, so there was nothing on
          screen to disagree with and the answer was always no. AI results are
          reviewed on /photos instead. */}

      <Field label="Notes (optional)">
        <TextArea value={notes} onChange={setNotes} placeholder="Remarks, discrepancy, etc." />
      </Field>

      <SubmitButton onClick={submit} disabled={busy || !selTx || !readyForExit} color={C.teal}>
        {busy ? 'Completing…' : !selTx ? 'Log exit & tag photo' : readyForExit ? 'Log exit & tag photo' : 'Not ready for exit'}
      </SubmitButton>
    </FormShell>
  );
}